/*
 * Heritage Vault — Save-to-Vault wishlist store.
 *
 * Local-first wishlist. Items live in localStorage on the device;
 * the same payload is mirrored to MDJ via the integrations webhook
 * (when a vault endpoint is configured) so the desk can see what
 * pieces partners are saving for remarketing. No customer-record
 * write happens client-side because the Storefront API can't write
 * customer metafields — the webhook is the authoritative remarketing
 * surface.
 *
 * Storage shape:
 *   localStorage["mdj.vault.items"] = [
 *     {
 *       id: <product.id>,
 *       handle: <product.handle>,
 *       title: string,
 *       image: string,         // CDN url, ~600w
 *       era: string,           // eyebrow line
 *       sku: string,
 *       url: string,           // /products/<handle>
 *       savedAt: number        // unix ms
 *     },
 *     ...
 *   ]
 *
 * Public API on window.MDJVault:
 *   getAll(): item[]
 *   has(id): boolean
 *   add(item): void           — also fires webhook + event
 *   remove(id): void          — also fires webhook + event
 *   toggle(item): boolean     — returns new saved state
 *   subscribe(fn): unsub      — fn called on every state change
 *
 * Custom events (dispatched on document):
 *   mdj:vault-changed { detail: { items, action, item } }
 */

(function () {
  const KEY = 'mdj.vault.items';
  const subscribers = new Set();

  function safeStorage() {
    try {
      const k = '__mdj_vault_test__';
      window.localStorage.setItem(k, k);
      window.localStorage.removeItem(k);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function read() {
    const s = safeStorage();
    if (!s) return [];
    try {
      const raw = s.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    const s = safeStorage();
    if (!s) return;
    try {
      s.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      /* quota exceeded — surface via console only */
      if (window.console) console.warn('MDJVault: localStorage write failed', e);
    }
  }

  function notify(action, item, items) {
    const detail = { action: action, item: item, items: items };
    document.dispatchEvent(new CustomEvent('mdj:vault-changed', { detail: detail }));
    subscribers.forEach((fn) => {
      try { fn(detail); } catch (e) { /* user fn shouldn't break the store */ }
    });
  }

  function fireWebhook(action, item, items) {
    if (!window.MDJIntegrations || !window.MDJIntegrations.send) return;
    window.MDJIntegrations.send('vault', {
      event: 'vault.' + action,
      product: item,
      vault_size_after: items.length
    });
  }

  function getAll() {
    return read();
  }

  function has(id) {
    if (id == null) return false;
    return read().some((it) => String(it.id) === String(id));
  }

  function add(item) {
    if (!item || item.id == null) return;
    const items = read();
    if (items.some((it) => String(it.id) === String(item.id))) return;
    const stamped = Object.assign({}, item, { savedAt: Date.now() });
    items.unshift(stamped);
    write(items);
    notify('add', stamped, items);
    fireWebhook('add', stamped, items);
  }

  function remove(id) {
    if (id == null) return;
    const items = read();
    const idx = items.findIndex((it) => String(it.id) === String(id));
    if (idx < 0) return;
    const [removed] = items.splice(idx, 1);
    write(items);
    notify('remove', removed, items);
    fireWebhook('remove', removed, items);
  }

  function toggle(item) {
    if (!item || item.id == null) return false;
    if (has(item.id)) {
      remove(item.id);
      return false;
    }
    add(item);
    return true;
  }

  function subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }

  window.MDJVault = { getAll, has, add, remove, toggle, subscribe };
})();
